import {
    chartRefreshScheduler,
    computeStaleState,
    formatAge,
    FrameBudgetTracker,
    measureFrame,
    RingBuffer,
    getStaleThresholdMs,
    setSnapshot,
    getSnapshot,
    reportChartRenderMetric,
} from '../../charts/engine';
import { renderAreaChart, renderDonutChart, renderHeatmapGrid, renderLineChart } from '../../charts/primitives';
import { apiFetch } from '../../services/api-client';
import { fetchMLFeatures, fetchMLRegime, fetchMLStrategy, type MLRegimeResponse, type MLStrategyResponse } from '../../services/ml-api';
import { isFeatureEnabled } from '../../services/runtime-config';

type PerformanceData = {
    dailyAccuracy: number[];
};

type StrategyState = {
    strategy: MLStrategyResponse | null;
    regime: MLRegimeResponse | null;
    features: number[];
    performance: PerformanceData | null;
};

type StrategySnapshot = {
    state: StrategyState;
    confidenceSeries: number[];
    driftSeries: number[];
    entropySeries: number[];
    confidenceVolSeries: number[];
    driftVelocitySeries: number[];
    lastUpdatedAt: number;
};

const FEATURE_LABELS = [
    'FII Net',
    'EMA9/21',
    'ADX',
    'RSI',
    'VIX',
    'MACD',
    'PCR',
    'Bollinger',
    'Volume',
    'Global Risk',
    'AD Ratio',
    'Delivery',
];

const teardownMap = new WeakMap<HTMLElement, () => void>();

function formatINR(value: number): string {
    return `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function getRegimeBreakdown(regime: MLRegimeResponse | null): { labels: string[]; values: number[] } {
    if (!regime?.probabilities) {
        return { labels: ['Unknown'], values: [100] };
    }

    const entries = Object.entries(regime.probabilities)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 4);

    if (entries.length === 0) return { labels: ['Unknown'], values: [100] };
    return {
        labels: entries.map(([k]) => k.replace(/_/g, ' ')),
        values: entries.map(([, v]) => Math.max(0, v)),
    };
}

function toFeatureCells(features: number[]): Array<{ x: number; y: number; value: number }> {
    const base = features.slice(0, 12);
    return base.map((value, idx) => ({
        x: idx % 6,
        y: Math.floor(idx / 6),
        value: Math.max(-5, Math.min(5, value)),
    }));
}

function toUncertaintySeries(values: number[], windowSize: number): number[] {
    if (values.length === 0) return [];
    return values.map((_, idx) => {
        const start = Math.max(0, idx - windowSize + 1);
        const window = values.slice(start, idx + 1);
        if (window.length <= 1) return 0;
        const mean = window.reduce((sum, value) => sum + value, 0) / window.length;
        const variance = window.reduce((sum, value) => {
            const delta = value - mean;
            return sum + (delta * delta);
        }, 0) / window.length;
        return Math.sqrt(variance);
    });
}

function toFeatureDriftCells(current: number[], previous: number[]): Array<{ x: number; y: number; value: number }> {
    const base = current.slice(0, 12);
    return base.map((value, idx) => {
        const prev = Number(previous[idx] || 0);
        const delta = Number(value || 0) - prev;
        return {
            x: idx % 6,
            y: Math.floor(idx / 6),
            value: Math.max(-5, Math.min(5, delta * 2)),
        };
    });
}

function calcFeatureDriftScore(current: number[], previous: number[]): number {
    if (current.length === 0 || previous.length === 0) return 0;
    const count = Math.min(12, current.length, previous.length);
    if (count <= 0) return 0;
    let total = 0;
    for (let i = 0; i < count; i += 1) {
        total += Math.abs(Number(current[i] || 0) - Number(previous[i] || 0));
    }
    return total / count;
}

function getRegimeEntropy(regime: MLRegimeResponse | null): number {
    const probs = Object.values(regime?.probabilities || {}).map((v) => Math.max(0, Number(v || 0)) / 100);
    const norm = probs.filter(v => v > 0);
    if (norm.length === 0) return 0;
    const entropy = -norm.reduce((sum, p) => sum + (p * Math.log2(p)), 0);
    const maxEntropy = Math.log2(norm.length);
    if (maxEntropy <= 0) return 0;
    return (entropy / maxEntropy) * 100;
}

export function renderPhase2StrategyEngine(container: HTMLElement): void {
    if (!isFeatureEnabled('charts_v2_engine')) {
        container.innerHTML = '<div class="wm-empty">charts_v2_engine flag is disabled.</div>';
        return;
    }

    teardownMap.get(container)?.();

    container.innerHTML = `
      <div class="v2-chart-card glass-card">
        <div class="card-header">
          <h3>Phase 2 Engine: Strategy Intelligence</h3>
          <span class="text-xs text-muted">Tier C refresh</span>
        </div>

        <div class="v2-chart-grid">
          <div class="v2-chart-slot">
            <div class="v2-chart-title">Confidence Timeline</div>
            <canvas id="v2-strategy-confidence" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Regime Probability Split</div>
            <canvas id="v2-strategy-regime-donut" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Feature Pulse Grid</div>
            <canvas id="v2-strategy-feature-grid" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Performance Accuracy Trend</div>
            <canvas id="v2-strategy-accuracy" class="v2-chart-canvas"></canvas>
          </div>

                    <div class="v2-chart-slot">
                        <div class="v2-chart-title">Confidence Uncertainty</div>
                        <canvas id="v2-strategy-uncertainty" class="v2-chart-canvas"></canvas>
                    </div>

                    <div class="v2-chart-slot">
                        <div class="v2-chart-title">Feature Drift Grid</div>
                        <canvas id="v2-strategy-drift-grid" class="v2-chart-canvas"></canvas>
                    </div>

                    <div class="v2-chart-slot">
                        <div class="v2-chart-title">Regime Entropy Pulse</div>
                        <canvas id="v2-strategy-entropy" class="v2-chart-canvas"></canvas>
                    </div>

                    <div class="v2-chart-slot">
                        <div class="v2-chart-title">Confidence Volatility Pulse</div>
                        <canvas id="v2-strategy-confidence-vol" class="v2-chart-canvas"></canvas>
                    </div>

                    <div class="v2-chart-slot">
                        <div class="v2-chart-title">Drift Velocity Pulse</div>
                        <canvas id="v2-strategy-drift-velocity" class="v2-chart-canvas"></canvas>
                    </div>

          <div class="v2-chart-slot v2-chart-slot--wide">
            <div class="v2-chart-title">Live Strategy Board</div>
            <div id="v2-strategy-board" class="v2-sector-board"></div>
          </div>
        </div>

        <div class="v2-chart-meta">
          <span id="v2-strategy-freshness" class="text-xs">Waiting for live strategy feed...</span>
          <span id="v2-strategy-frame" class="text-xs text-muted" style="margin-left: var(--space-3);">Frame: --</span>
        </div>

        <div id="v2-strategy-stale" class="v2-stale-overlay" style="display:none;">
          Strategy feed delayed. Rendering cached snapshot.
        </div>
      </div>
    `;

    const confidenceCanvas = container.querySelector('#v2-strategy-confidence') as HTMLCanvasElement | null;
    const regimeDonut = container.querySelector('#v2-strategy-regime-donut') as HTMLCanvasElement | null;
    const featureGrid = container.querySelector('#v2-strategy-feature-grid') as HTMLCanvasElement | null;
    const accuracyCanvas = container.querySelector('#v2-strategy-accuracy') as HTMLCanvasElement | null;
    const uncertaintyCanvas = container.querySelector('#v2-strategy-uncertainty') as HTMLCanvasElement | null;
    const driftGrid = container.querySelector('#v2-strategy-drift-grid') as HTMLCanvasElement | null;
    const entropyCanvas = container.querySelector('#v2-strategy-entropy') as HTMLCanvasElement | null;
    const confidenceVolCanvas = container.querySelector('#v2-strategy-confidence-vol') as HTMLCanvasElement | null;
    const driftVelocityCanvas = container.querySelector('#v2-strategy-drift-velocity') as HTMLCanvasElement | null;
    const boardNode = container.querySelector('#v2-strategy-board') as HTMLElement | null;
    const freshnessNode = container.querySelector('#v2-strategy-freshness') as HTMLElement | null;
    const frameNode = container.querySelector('#v2-strategy-frame') as HTMLElement | null;
    const staleNode = container.querySelector('#v2-strategy-stale') as HTMLElement | null;

    if (!confidenceCanvas || !regimeDonut || !featureGrid || !accuracyCanvas || !uncertaintyCanvas || !driftGrid || !entropyCanvas || !confidenceVolCanvas || !driftVelocityCanvas || !boardNode || !freshnessNode || !frameNode || !staleNode) return;

    const snapshotKey = 'phase2:strategy-engine';
    const staleThresholdMs = getStaleThresholdMs('C');
    const frameTracker = new FrameBudgetTracker(180, 16);
    const confidenceSeries = new RingBuffer<number>(180);
    const driftSeries = new RingBuffer<number>(180);
    const entropySeries = new RingBuffer<number>(180);
    const confidenceVolSeries = new RingBuffer<number>(180);
    const driftVelocitySeries = new RingBuffer<number>(180);

    let lastUpdatedAt: number | null = null;
    let previousFeatures: number[] = [];
    let lastState: StrategyState = {
        strategy: null,
        regime: null,
        features: [],
        performance: null,
    };

    const drawBoard = (
        state: StrategyState,
        diagnostics: { entropy: number; confidenceVolatility: number; drift: number },
    ): void => {
        const strategy = state.strategy;
        const regime = state.regime;

        if (!strategy && !regime) {
            boardNode.innerHTML = '<div class="wm-empty">No live strategy signal available.</div>';
            return;
        }

        const rows: string[] = [];

        if (strategy) {
            rows.push(`<div class="terminal-list__row"><span>Strategy</span><strong>${strategy.strategyLabel || strategy.strategy}</strong></div>`);
            rows.push(`<div class="terminal-list__row"><span>Confidence</span><strong>${(strategy.confidence || 0).toFixed(1)}%</strong></div>`);
            rows.push(`<div class="terminal-list__row"><span>Entry</span><strong>${formatINR(strategy.entry?.min || 0)} - ${formatINR(strategy.entry?.max || 0)}</strong></div>`);
            rows.push(`<div class="terminal-list__row"><span>Target</span><strong>${formatINR(strategy.target?.min || 0)} - ${formatINR(strategy.target?.max || 0)}</strong></div>`);
            rows.push(`<div class="terminal-list__row"><span>Stop Loss</span><strong class="terminal-text--down">${formatINR(strategy.stopLoss || 0)}</strong></div>`);
            rows.push(`<div class="terminal-list__row"><span>Risk:Reward</span><strong>${(strategy.riskReward || 0).toFixed(2)}</strong></div>`);
            rows.push(`<div class="terminal-list__row"><span>Model Source</span><strong>${strategy.modelSource || 'unknown'}</strong></div>`);
        }

        if (regime) {
            rows.push(`<div class="terminal-list__row"><span>Regime</span><strong>${regime.label || regime.regime}</strong></div>`);
            rows.push(`<div class="terminal-list__row"><span>Regime Confidence</span><strong>${(regime.confidence || 0).toFixed(1)}%</strong></div>`);
        }

        const entropyTone = diagnostics.entropy >= 70 ? 'terminal-text--down' : diagnostics.entropy >= 45 ? '' : 'terminal-text--up';
        const volatilityTone = diagnostics.confidenceVolatility >= 8 ? 'terminal-text--down' : diagnostics.confidenceVolatility >= 4 ? '' : 'terminal-text--up';
        const driftTone = diagnostics.drift >= 1.5 ? 'terminal-text--down' : diagnostics.drift >= 0.8 ? '' : 'terminal-text--up';

        rows.push(`<div class="terminal-list__row"><span>Regime Entropy</span><strong class="${entropyTone}">${diagnostics.entropy.toFixed(1)}%</strong></div>`);
        rows.push(`<div class="terminal-list__row"><span>Confidence Volatility</span><strong class="${volatilityTone}">${diagnostics.confidenceVolatility.toFixed(2)}</strong></div>`);
        rows.push(`<div class="terminal-list__row"><span>Feature Drift Score</span><strong class="${driftTone}">${diagnostics.drift.toFixed(2)}</strong></div>`);

        const featureLegend = state.features.slice(0, 6)
            .map((value, idx) => `${FEATURE_LABELS[idx]}: ${value.toFixed(2)}`)
            .join(' | ');

        boardNode.innerHTML = `${rows.join('')}<div class="text-xs text-muted" style="margin-top: var(--space-2);">${featureLegend || 'No feature payload available.'}</div>`;
    };

    const draw = (state: StrategyState): void => {
        const sample = measureFrame('strategy-engine', () => {
            const confidenceValues = confidenceSeries.toArray();
            renderLineChart(confidenceCanvas, confidenceValues, '#ff9f43');

            const regimeBreakdown = getRegimeBreakdown(state.regime);
            renderDonutChart(regimeDonut, regimeBreakdown.values, ['#15b57a', '#d94736', '#5aa6ff', '#ff9f43']);

            renderHeatmapGrid(featureGrid, toFeatureCells(state.features), 6, 2);

            const accuracySeries = state.performance?.dailyAccuracy || [];
            renderLineChart(accuracyCanvas, accuracySeries, '#5aa6ff');

            const uncertaintySeries = toUncertaintySeries(confidenceValues, 20);
            renderAreaChart(uncertaintyCanvas, uncertaintySeries, '#f5c542', 'rgba(245,197,66,0.2)');

            renderHeatmapGrid(driftGrid, toFeatureDriftCells(state.features, previousFeatures), 6, 2);

            renderLineChart(entropyCanvas, entropySeries.toArray(), '#5aa6ff');
            renderLineChart(confidenceVolCanvas, confidenceVolSeries.toArray(), '#ff9f43');
            renderLineChart(driftVelocityCanvas, driftVelocitySeries.toArray(), '#15b57a');

            const latestVolatility = uncertaintySeries.length > 0 ? uncertaintySeries[uncertaintySeries.length - 1] : 0;
            const latestDrift = driftSeries.toArray().length > 0
                ? driftSeries.toArray()[driftSeries.toArray().length - 1]
                : calcFeatureDriftScore(state.features, previousFeatures);
            const entropy = getRegimeEntropy(state.regime);

            drawBoard(state, {
                entropy,
                confidenceVolatility: latestVolatility,
                drift: latestDrift,
            });
        });

        frameTracker.push(sample.label, sample.durationMs);
        const avg = frameTracker.getAverageMs();
        const within = frameTracker.isWithinBudget(sample.durationMs);
        frameNode.textContent = `Frame: ${sample.durationMs.toFixed(2)}ms | avg ${avg.toFixed(2)}ms | budget ${within ? 'OK' : 'OVER'}`;

        reportChartRenderMetric({
            id: 'phase2-strategy',
            label: 'Strategy Intelligence',
            latestMs: sample.durationMs,
            averageMs: avg,
            budgetMs: frameTracker.getBudgetMs(),
            withinBudget: within,
        });

        const stale = computeStaleState(lastUpdatedAt, staleThresholdMs);
        staleNode.style.display = stale.stale ? 'flex' : 'none';
        freshnessNode.textContent = stale.stale
            ? `Stale: ${formatAge(stale.ageMs)} old`
            : `Live update ${new Date(lastUpdatedAt || Date.now()).toLocaleTimeString('en-IN')}`;
    };

    const persist = (): void => {
        setSnapshot<StrategySnapshot>(snapshotKey, {
            state: lastState,
            confidenceSeries: confidenceSeries.toArray(),
            driftSeries: driftSeries.toArray(),
            entropySeries: entropySeries.toArray(),
            confidenceVolSeries: confidenceVolSeries.toArray(),
            driftVelocitySeries: driftVelocitySeries.toArray(),
            lastUpdatedAt: lastUpdatedAt || Date.now(),
        });
    };

    const update = async () => {
        try {
            const [strategyResult, regimeResult, featuresResult, perfResult] = await Promise.allSettled([
                fetchMLStrategy(),
                fetchMLRegime(),
                fetchMLFeatures(),
                apiFetch<PerformanceData>('/ml/performance', { cacheTTL: 300 }),
            ]);

            const strategy = strategyResult.status === 'fulfilled' ? strategyResult.value : null;
            const regime = regimeResult.status === 'fulfilled' ? regimeResult.value : null;
            const features = featuresResult.status === 'fulfilled' ? featuresResult.value : [];
            const performance = perfResult.status === 'fulfilled' ? perfResult.value.data : null;

            if (!strategy && !regime && features.length === 0 && !performance) {
                throw new Error('no strategy payload');
            }

            const confidence = strategy?.confidence ?? regime?.confidence ?? 0;
            confidenceSeries.push(confidence);
            const driftScore = calcFeatureDriftScore(features, previousFeatures);
            const prevDrift = driftSeries.toArray().slice(-1)[0] ?? driftScore;
            driftSeries.push(driftScore);
            driftVelocitySeries.push(driftScore - prevDrift);
            entropySeries.push(getRegimeEntropy(regime));

            const confidenceValues = confidenceSeries.toArray();
            const volatilitySeries = toUncertaintySeries(confidenceValues, 20);
            const latestConfidenceVol = volatilitySeries.length > 0 ? volatilitySeries[volatilitySeries.length - 1] : 0;
            confidenceVolSeries.push(latestConfidenceVol);

            lastState = { strategy, regime, features, performance };
            lastUpdatedAt = Date.now();
            persist();
            draw(lastState);
            previousFeatures = features.slice(0, 12);
        } catch {
            if (lastState.strategy || lastState.regime || lastState.features.length > 0 || lastState.performance) {
                draw(lastState);
            }
        }
    };

    const persisted = getSnapshot<StrategySnapshot>(snapshotKey);
    if (persisted?.state) {
        lastState = persisted.state;
        persisted.confidenceSeries.forEach((value) => confidenceSeries.push(value));
        persisted.driftSeries?.forEach((value) => driftSeries.push(value));
        persisted.entropySeries?.forEach((value) => entropySeries.push(value));
        persisted.confidenceVolSeries?.forEach((value) => confidenceVolSeries.push(value));
        persisted.driftVelocitySeries?.forEach((value) => driftVelocitySeries.push(value));
        lastUpdatedAt = persisted.lastUpdatedAt;
        previousFeatures = persisted.state.features.slice(0, 12);
        draw(lastState);
    }

    const taskId = `phase2-strategy-${Math.random().toString(36).slice(2)}`;
    const unregister = chartRefreshScheduler.register(taskId, 'C', update, 15000);

    teardownMap.set(container, () => {
        unregister();
    });
}
